<section class="change-review" aria-label="Browser-local notice comparison">
  <div class="review-actions" aria-label="Local comparison actions">
    <nav class="review-sections" aria-label="Comparison sections">
      <button
        uiButton="secondary"
        type="button"
        [attr.aria-pressed]="section === 'changes'"
        (click)="showSection('changes')"
      >
        Changes <span class="section-count">{{ changeCount }}</span>
      </button>
      <button
        uiButton="secondary"
        type="button"
        [attr.aria-pressed]="section === 'pdfs'"
        [disabled]="!baseline"
        (click)="showSection('pdfs')"
      >
        PDF comparison
        @if (exactProofs) {
          <span class="section-count">Ready</span>
        }
      </button>
    </nav>
    <div class="action-buttons">
      <button
        uiButton="secondary"
        type="button"
        (click)="capture()"
        [disabled]="disabled || !canCapture"
        [attr.aria-label]="
          baseline
            ? 'Replace baseline with saved source'
            : 'Capture saved baseline'
        "
      >
        {{ baseline ? "Replace baseline" : "Capture baseline" }}
      </button>
      <button
        uiButton="primary"
        type="button"
        aria-label="Render comparison PDFs"
        (click)="render()"
        [disabled]="
          disabled ||
          !canRender ||
          !baseline ||
          !composition ||
          !comparison?.compatible
        "
      >
        Render PDFs
      </button>
      <button
        uiButton="secondary"
        type="button"
        (click)="clear()"
        [disabled]="disabled || !baseline"
      >
        Clear baseline
      </button>
      @if (canUndoBaseline) {
        <button
          uiButton="secondary"
          type="button"
          (click)="undo()"
          [disabled]="disabled"
        >
          Undo baseline change
        </button>
      }
      <span class="session-label">Session only</span>
    </div>
  </div>
  <div #reviewBody class="review-body" [class.pdf-active]="section === 'pdfs'">
    @if (baselineDiagnostics.length) {
      <details class="source-details" aria-label="Baseline verification issues">
        <summary>Baseline issues · {{ baselineDiagnostics.length }}</summary>
        <p class="muted">
          These issues refer to a captured baseline. Your current source is
          unchanged.
        </p>
        <ul>
          @for (diagnostic of baselineDiagnostics; track $index) {
            <li>
              <p>{{ diagnostic.message }}</p>
              <p>{{ diagnostic.action }}</p>
              <details>
                <summary>Technical details</summary>
                <dl>
                  <dt>Path</dt>
                  <dd>{{ diagnostic.path }}</dd>
                  <dt>Code</dt>
                  <dd>{{ diagnostic.code }}</dd>
                </dl>
              </details>
            </li>
          }
        </ul>
      </details>
    }
    <section
      class="review-panel"
      [hidden]="section !== 'changes'"
      aria-label="Composition changes"
    >
      <h3 #changesHeading tabindex="-1" class="visually-hidden">
        Composition changes: {{ changeCount }}
      </h3>
      @if (!baseline) {
        <p class="empty-state" role="status">
          No baseline captured. Save locally, then capture the saved source to
          begin.
        </p>
      } @else {
        @if (!composition) {
          <p class="comparison-warning" role="status">
            Current source is not available for structured comparison. Open
            Fields to inspect and validate the retained source.
          </p>
          <button
            uiButton="secondary"
            type="button"
            (click)="fields()"
            [disabled]="disabled"
          >
            Inspect source in Fields
          </button>
        } @else if (!comparison) {
          <p class="empty-state" role="status">
            Structured comparison is not available yet.
          </p>
        } @else if (!comparison.compatible) {
          <p class="comparison-warning" role="status">
            Comparison unavailable. {{ comparison.message }}
          </p>
          <button
            uiButton="secondary"
            type="button"
            (click)="fields()"
            [disabled]="disabled"
          >
            Inspect source in Fields
          </button>
        } @else {
          @if (!comparison.changes.length) {
            <p class="empty-state" role="status">
              No composition changes from the captured baseline.
            </p>
          } @else {
            <div class="changes" aria-label="Composition changes">
              @for (group of groups; track group) {
                <section
                  class="change-group"
                  [attr.aria-label]="group + ' changes'"
                >
                  <h4>{{ group }}</h4>
                  <div
                    class="change-table-scroll"
                    tabindex="0"
                    [attr.aria-label]="group + ' change details'"
                  >
                    <table>
                      <thead>
                        <tr>
                          <th scope="col">Change</th>
                          <th scope="col">Baseline</th>
                          <th scope="col">Current</th>
                          <th scope="col">Edit</th>
                        </tr>
                      </thead>
                      <tbody>
                        @for (change of changesFor(group); track change.id) {
                          <tr [attr.data-change-id]="change.id">
                            <th scope="row">{{ change.label }}</th>
                            <td class="change-value">{{ change.before }}</td>
                            <td class="change-value">{{ change.after }}</td>
                            <td>
                              @if (
                                change.blockId && canEditBlock(change.blockId)
                              ) {
                                <button
                                  uiButton="secondary"
                                  type="button"
                                  (click)="edit(change.blockId)"
                                  [disabled]="disabled"
                                  [attr.aria-label]="'Edit ' + change.label"
                                >
                                  Edit block
                                </button>
                              } @else {
                                <button
                                  uiButton="secondary"
                                  type="button"
                                  (click)="fields()"
                                  [disabled]="disabled"
                                  [attr.aria-label]="
                                    'Inspect ' + change.label + ' in Fields'
                                  "
                                >
                                  Fields
                                </button>
                              }
                            </td>
                          </tr>
                        }
                      </tbody>
                    </table>
                  </div>
                </section>
              }
            </div>
          }
        }
      }
      <details class="source-details">
        <summary>Session baseline details</summary>
        <p class="muted review-guidance">
          Session only. Capture a validated local save, then edit to compare.
          The baseline is cleared when this editor session ends. PDFs capture
          the selected example and context at render time. This comparison does
          not record production review or publication.
        </p>
        @if (baseline) {
          <dl>
            <dt>Baseline source hash</dt>
            <dd data-review-baseline-hash>{{ baseline.sourceHash }}</dd>
            <dt>Current source hash</dt>
            <dd data-review-current-hash>
              {{ currentSourceHash || "Validation required" }}
            </dd>
            <dt>Captured renderer</dt>
            <dd>
              {{ baseline.rendererCode }} · {{ baseline.rendererVersion }}
            </dd>
            <dt>Captured contract</dt>
            <dd>{{ baseline.contractIdentity }}</dd>
            <dt>Captured scope</dt>
            <dd>
              {{ baseline.scopeTypeCode }} · {{ baseline.scopeKey || "Global" }}
            </dd>
          </dl>
        }
      </details>
    </section>
    <section
      class="review-panel pdf-panel"
      [hidden]="section !== 'pdfs'"
      aria-label="PDF comparison"
    >
      <h3 #pdfHeading tabindex="-1" class="visually-hidden">PDF comparison</h3>
      @if (!exactProofs) {
        <p class="empty-state" role="status">
          No matching comparison PDFs. Save the current source locally, then
          render both PDFs for the selected example.
        </p>
      }
      @if (exactProofs; as pair) {
        @let scale = proofScale;
        <div
          class="proof-navigation"
          role="group"
          aria-label="Comparison page controls"
          tabindex="0"
          (keydown)="pageKeydown($event)"
        >
          <button
            uiButton="secondary"
            type="button"
            (click)="setPage(pageNumber - 1)"
            [disabled]="pageNumber <= 1"
            aria-label="Previous comparison page"
          >
            Previous
          </button>
          <label
            >Page<select
              uiControl
              aria-label="Comparison page"
              name="comparison-page"
              [ngModel]="pageNumber"
              (ngModelChange)="setPage($event)"
            >
              @for (number of pageNumbers; track number) {
                <option [ngValue]="number">{{ number }}</option>
              }</select
            ><span>of {{ pageCount }}</span></label
          >
          <button
            uiButton="secondary"
            type="button"
            (click)="setPage(pageNumber + 1)"
            [disabled]="pageNumber >= pageCount"
            aria-label="Next comparison page"
          >
            Next
          </button>
          <label
            >Zoom<select
              uiControl
              aria-label="Comparison zoom"
              name="comparison-zoom"
              [ngModel]="zoom"
              (ngModelChange)="setZoom($event)"
            >
              <option [ngValue]="'fit-page'">Fit page</option>
              <option [ngValue]="'fit-width'">Fit width</option>
              <option [ngValue]="50">50%</option>
              <option [ngValue]="75">75%</option>
              <option [ngValue]="100">100%</option>
            </select></label
          >
          @if (scale !== null) {
            <span class="zoom-value" aria-label="Actual comparison zoom"
              >{{ scale * 100 | number: "1.0-0" }}%</span
            >
          }
          <span class="visually-hidden" aria-live="polite"
            >Comparison page {{ pageNumber }} of {{ pageCount }}</span
          >
        </div>
        <div class="proof-pair" aria-label="Captured comparison PDFs">
          @for (side of sides; track side) {
            <section
              class="proof-side"
              [attr.aria-label]="
                side === 'baseline' ? 'Baseline PDF' : 'Current PDF'
              "
            >
              <div class="proof-heading">
                <h4>{{ side === "baseline" ? "Baseline" : "Current" }} PDF</h4>
                <button
                  uiButton="secondary"
                  type="button"
                  (click)="download(side)"
                  [disabled]="disabled"
                  [attr.aria-label]="'Download ' + side + ' PDF'"
                >
                  Download PDF
                </button>
              </div>
              <p class="proof-caption">
                {{ pair[side].isSynthetic ? "Synthetic" : "Captured" }} ·
                {{ pair[side].exampleCode }} · {{ pair[side].pageCount }}
                {{ pair[side].pageCount === 1 ? "page" : "pages" }}
              </p>
              <details
                #comparisonDetails
                class="proof-details"
                (keydown.escape)="
                  comparisonDetails.open = false;
                  comparisonSummary.focus();
                  $event.preventDefault();
                  $event.stopPropagation()
                "
              >
                <summary #comparisonSummary>
                  {{ side === "baseline" ? "Baseline" : "Current" }} captured
                  proof details
                </summary>
                <dl>
                  <dt>Source hash</dt>
                  <dd>{{ pair[side].sourceHash }}</dd>
                  <dt>PDF hash</dt>
                  <dd>{{ pair[side].pdfHash }}</dd>
                  <dt>Captured context hash</dt>
                  <dd>{{ pair[side].contextHash }}</dd>
                  <dt>Renderer</dt>
                  <dd>
                    {{ pair[side].rendererCode }} ·
                    {{ pair[side].rendererVersion }}
                  </dd>
                  <dt>Example</dt>
                  <dd>{{ pair[side].exampleCode }}</dd>
                  @if (pair[side].contextEvidence; as context) {
                    <dt>Context contract</dt>
                    <dd>
                      {{ context.reference.code }} ·
                      {{ context.reference.version }} ·
                      {{ context.reference.hash }}
                    </dd>
                    <dt>Captured context source</dt>
                    <dd>{{ context.sourceKind }} · {{ context.sourceKey }}</dd>
                    <dt>Captured context scope</dt>
                    <dd>
                      {{ context.scopeTypeCode }} ·
                      {{ context.scopeKey || "Global" }}
                    </dd>
                    <dt>Time zone</dt>
                    <dd>{{ context.timeZone }}</dd>
                  }
                </dl>
              </details>
              @if (proofPage(side); as page) {
                <div
                  class="proof-canvas"
                  phmsNoticeProofViewport
                  (viewportChange)="updateViewport(side, $event)"
                  tabindex="0"
                  [attr.aria-label]="
                    (side === 'baseline' ? 'Baseline' : 'Current') +
                    ' PDF page ' +
                    pageNumber +
                    ', scroll to inspect'
                  "
                >
                  <img
                    [src]="'data:image/png;base64,' + page.pngBase64"
                    [alt]="
                      (side === 'baseline' ? 'Baseline' : 'Current') +
                      ' PDF page ' +
                      pageNumber
                    "
                    [style.visibility]="scale === null ? 'hidden' : null"
                    [style.width.px]="page.widthPx * (scale ?? 0)"
                    [style.height.px]="page.heightPx * (scale ?? 0)"
                  />
                </div>
              } @else {
                <p class="missing-page" role="status">
                  No {{ side }} page {{ pageNumber }}. This PDF has
                  {{ pair[side].pageCount }}
                  {{ pair[side].pageCount === 1 ? "page" : "pages" }}.
                </p>
              }
            </section>
          }
        </div>
      }
    </section>
  </div>
</section>
